import { auth } from '../../config/firebase';
import { onAuthStateChanged, setPersistence, browserLocalPersistence, browserSessionPersistence } from 'firebase/auth';
import { useAuthStore } from '../../store/authStore';
import { useNotificationStore } from '../../store/notificationStore';
import { usePointsStore } from '../../store/pointsStore';
import { useThemeStore } from '../../store/themeStore';
import { getPersistedUserData } from './userPersistence';
import { getUserPoints } from '../userService';

export const cleanupAllStorage = () => {
  // Clear Zustand stores
  useAuthStore.getState().clearState();
  useNotificationStore.getState().clearAll();
  usePointsStore.getState().resetPoints();
  useThemeStore.getState().reset();

  // Clear localStorage except theme and auth persistence
  const keysToKeep = ['theme-storage', 'auth-persistence'];
  Object.keys(localStorage).forEach(key => {
    if (!keysToKeep.includes(key)) {
      localStorage.removeItem(key);
    }
  });

  // Clear sessionStorage
  sessionStorage.clear();
};

export const setPersistenceMode = async (rememberMe: boolean) => {
  try {
    // Vérifier que auth n'est pas null avant d'appeler setPersistence
    if (!auth) {
      throw new Error('Auth is not initialized');
    }
    
    const persistenceType = rememberMe ? browserLocalPersistence : browserSessionPersistence;
    await setPersistence(auth, persistenceType);
    localStorage.setItem('auth-persistence', rememberMe ? 'local' : 'session');
  } catch (error) {
    console.error('Error setting persistence:', error);
    throw error;
  }
};

export const restoreAuthSession = async () => {
  const { setUser, setLoading, initializeUser, checkAdminStatus, initialized, setInitialized } = useAuthStore.getState();
  const { setPoints } = usePointsStore.getState();
  
  if (initialized) {
    return;
  }
  
  try {
    setLoading(true);
    
    // Check saved persistence mode
    const persistenceMode = localStorage.getItem('auth-persistence');
    if (persistenceMode && auth) {
      await setPersistence(auth, persistenceMode === 'local' ? browserLocalPersistence : browserSessionPersistence);
    }

    // Vérifier que auth n'est pas null avant d'appeler onAuthStateChanged
    if (!auth) {
      throw new Error('Auth is not initialized');
    }

    return new Promise((resolve) => {
      const unsubscribe = onAuthStateChanged(auth, async (user) => {
        try {
          if (user) {
            // Force re-initialization on each session restore, especially important for mobile
            await initializeUser(user);
            await checkAdminStatus(user.uid);
            const points = await getUserPoints(user.uid);
            setPoints(points);
            
            const persistedData = await getPersistedUserData(user.uid);
            if (persistedData) {
              await setUser({
                ...user,
                ...persistedData
              });
            } else {
              await setUser(user);
            }
          } else {
            cleanupAllStorage();
            await setUser(null);
          }
        } catch (error) {
          console.error('Auth persistence error:', error);
          cleanupAllStorage();
          await setUser(null);
        } finally {
          setLoading(false);
          setInitialized(true);
          resolve(true);
        }
      });

      // Clean up subscription on unmount
      return () => unsubscribe();
    });
  } catch (error) {
    console.error('Error restoring auth session:', error);
    cleanupAllStorage();
    setLoading(false);
    setInitialized(true);
    throw error;
  }
};